import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  GeneticMaterialService,
  PeriodService,
  PlantationTypeService,
  PropertyService,
  ReasonService,
  SpacingService,
  SpeciesService,
  StandAreaMaintenanceTypeService,
  StandTypeService,
} from 'cbd-proxies';
import { DEFAULT_MAX_RESULT_COUNT, ZnSelectQueryParams } from '@zendra/shared';
import type { SpeciesDto } from 'projects/cbd-proxies/src/lib/general/dtos/species/models';

export interface GeneticMaterialSelectOption {
  key: string;
  value: string;
  species?: SpeciesDto | null;
}

@Injectable({
  providedIn: 'root',
})
export class StandCatalogs {
  private readonly propertyService = inject(PropertyService);
  private readonly standTypeService = inject(StandTypeService);
  private readonly periodService = inject(PeriodService);
  private readonly reasonService = inject(ReasonService);
  private readonly adjustmentTypesService = inject(StandAreaMaintenanceTypeService);
  private readonly plantationTypeService = inject(PlantationTypeService);
  private readonly spacingService = inject(SpacingService);
  private readonly geneticMaterialService = inject(GeneticMaterialService);
  private readonly speciesService = inject(SpeciesService);


    loadProperty(
    params?: ZnSelectQueryParams
  ): Observable<{ items: { key: string; value: string }[]; totalCount: number }> {
    return this.propertyService
      .getList({
        name: params?.search,
        skipCount: params?.skipCount,
        maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
      })
      .pipe(
        map(response => ({
          items: response.items.map(item => ({
            key: String(item.id),
            value: item.name ?? String(item.id),
          })),
          totalCount: response.totalCount,
        })),
      );
  }

  loadsSandType(
    params?: ZnSelectQueryParams
  ): Observable<{ items: { key: string; value: string }[]; totalCount: number }> {
    return this.standTypeService
      .getList({
        name: params?.search,
        skipCount: params?.skipCount,
        maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
      })
      .pipe(
        map(response => ({
          items: response.items.map(item => ({
            key: String(item.id),
            value: item.name ?? String(item.id),
          })),
          totalCount: response.totalCount,
        })),
      );
  }

  loadPeriod(
    params?: ZnSelectQueryParams,
    periodTypeName?: string,
  ): Observable<{ items: { key: string; value: string }[]; totalCount: number }> {
    if (params?.ids?.length && !params.search) {
      return this.periodService.getListByIds({ ids: params.ids.map(Number) }).pipe(
        map(items => ({
          items: items.map(item => ({
            key: String(item.id),
            value: item.name ?? String(item.id),
          })),
          totalCount: items.length,
        })),
      );
    }

    return this.periodService
      .getList({
        periodTypeName,
        name: params?.search,
        skipCount: params?.skipCount,
        maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
      })
      .pipe(
        map(response => ({
          items: response.items.map(item => ({
            key: String(item.id),
            value: item.name ?? String(item.id),
          })),
          totalCount: response.totalCount,
        })),
      );
  }

  loadReason(
    params?: ZnSelectQueryParams,
    reasonGroupName?: string,
  ): Observable<{ items: { key: string; value: string }[]; totalCount: number }> {
    if (params?.ids?.length && !params.search) {
      return this.reasonService.getListByIds({ ids: params.ids.map(Number) }).pipe(
        map(items => ({
          items: items.map(item => ({
            key: String(item.id),
            value: item.name ?? String(item.id),
          })),
          totalCount: items.length,
        })),
      );
    }

    return this.reasonService
      .getList({
        name: params?.search,
        reasonGroupName,
        skipCount: params?.skipCount,
        maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
      })
      .pipe(
        map(response => ({
          items: response.items.map(item => ({
            key: String(item.id),
            value: item.name ?? String(item.id),
          })),
          totalCount: response.totalCount,
        })),
      );
  }

  loadAdjustmentTypes(
    params?: ZnSelectQueryParams
  ): Observable<{ items: { key: string; value: string }[]; totalCount: number }> {
    if (params?.ids?.length && !params.search) {
      return this.adjustmentTypesService.getListByIds({ ids: params.ids.map(Number) }).pipe(
        map(items => ({
          items: items.map(item => ({
            key: String(item.id),
            value: item.name ?? String(item.id),
          })),
          totalCount: items.length,
        })),
      );
    }

    return this.adjustmentTypesService
      .getList({
        name: params?.search,
        skipCount: params?.skipCount,
        maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
      })
      .pipe(
        map(response => ({
          items: response.items.map(item => ({
            key: String(item.id),
            value: item.name ?? String(item.id),
          })),
          totalCount: response.totalCount,
        })),
      );
  }

  loadPlantingTypes(
    params?: ZnSelectQueryParams
  ): Observable<{ items: { key: string; value: string }[]; totalCount: number }> {
    return this.plantationTypeService
      .getList({
        name: params?.search,
        skipCount: params?.skipCount,
        maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
      })
      .pipe(
        map(response => ({
          items: response.items.map(item => ({
            key: String(item.id),
            value: item.name ?? String(item.id),
          })),
          totalCount: response.totalCount,
        })),
      );
  }

  loadSpacings(
    params?: ZnSelectQueryParams
  ): Observable<{ items: { key: string; value: string }[]; totalCount: number }> {
    return this.spacingService
      .getList({
        name: params?.search,
        skipCount: params?.skipCount,
        maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
      })
      .pipe(
        map(response => ({
          items: response.items.map(item => ({
            key: String(item.id),
            value: item.name ?? String(item.id),
          })),
          totalCount: response.totalCount,
        })),
      );
  }
  
  loadGeneticMaterials(
    params?: ZnSelectQueryParams
  ): Observable<{ items: GeneticMaterialSelectOption[]; totalCount: number }> {
    if (params?.ids?.length && !params.search && !params.preferCache) {
      return this.geneticMaterialService.getListByIds({ ids: params.ids.map(Number) }).pipe(
        map(items => ({
          items: items.map(item => ({
            key: String(item.id),
            value: item.name ?? String(item.id),
            species: item.species,
          })),
          totalCount: items.length,
        })),
      );
    }

    return this.geneticMaterialService
      .getList({
        name: params?.search,
        skipCount: params?.skipCount,
        maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
      })
      .pipe(
        map(response => ({
          items: response.items.map(item => ({
            key: String(item.id),
            value: item.name ?? String(item.id),
            species: item.species,
          })),
          totalCount: response.totalCount,
        })),
      );
  }

  loadSpecies(
    params?: ZnSelectQueryParams
  ): Observable<{ items: { key: string; value: string }[]; totalCount: number }> {
    if (params?.ids?.length && !params.search && !params.preferCache) {
      return this.speciesService.getListByIds({ ids: params.ids.map(Number) }).pipe(
        map(items => ({
          items: items.map(item => ({
            key: String(item.id),
            value: item.name ?? String(item.id),
          })),
          totalCount: items.length,
        })),
      );
    }

    return this.speciesService
      .getList({
        name: params?.search,
        skipCount: params?.skipCount,
        maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
      })
      .pipe(
        map(response => ({
          items: response.items.map(item => ({
            key: String(item.id),
            value: item.name ?? String(item.id),
          })),
          totalCount: response.totalCount,
        })),
      );
  }
}

